"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect, useRef } from "react";
import { useCart } from "@/hooks/useCart";
import { useWishlist } from "@/hooks/useWishlist";
import { useAuth } from "@/hooks/useAuth";
import { MegaMenu } from "@/components/MegaMenu";
import { SearchBar } from "@/components/SearchBar";
import { useCartUi } from "@/context/CartUiContext";
import { categoriesMeta } from "@/lib/categories-meta";

const navLinks = [
  { href: "/koleksiyon/yeni-urunler", label: "YENİ ÜRÜNLER" },
  { href: "/koleksiyon/cok-satanlar", label: "ÇOK SATANLAR" },
  { href: "/koleksiyon/indirimliler", label: "İNDİRİMLİLER🔥" },
  { href: "/koleksiyon/makyaj", label: "MAKYAJ" },
  { href: "/koleksiyon/cilt-bakimi", label: "CİLT BAKIMI" },
  { href: "/markalar", label: "MARKALAR" },
  { href: "/kategori", label: "KOLEKSİYONLAR" },
  { href: "/blog", label: "BLOG" },
];

const mobileLinks = [
  { href: "/urunler", label: "TÜM ÜRÜNLER" },
  { href: "/koleksiyon/yeni-urunler", label: "YENİ ÜRÜNLER" },
  { href: "/koleksiyon/cok-satanlar", label: "ÇOK SATANLAR" },
  { href: "/koleksiyon/indirimliler", label: "İNDİRİMLİLER🔥" },
  { href: "/koleksiyon/makyaj", label: "MAKYAJ" },
  { href: "/koleksiyon/cilt-bakimi", label: "CİLT BAKIMI" },
  { href: "/markalar", label: "MARKALAR" },
  { href: "/kategori", label: "KOLEKSİYONLAR" },
  { href: "/blog", label: "BLOG" },
];

function ChevronDownIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function UserIcon({ className = "h-6 w-6" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function HeartIcon({ className = "h-6 w-6" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

function BagIcon({ className = "h-6 w-6" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  );
}

export function Navbar() {
  const { count } = useCart();
  const { openCart } = useCartUi();
  const { count: wishCount } = useWishlist();
  const { isAuthenticated, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openMenu = () => {
    if (menuCloseTimer.current) clearTimeout(menuCloseTimer.current);
    setMenuOpen(true);
  };
  const scheduleMenuClose = () => {
    if (menuCloseTimer.current) clearTimeout(menuCloseTimer.current);
    menuCloseTimer.current = setTimeout(() => setMenuOpen(false), 150);
  };

  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const drawer = document.getElementById("mobile-drawer");
    const closeBtn = drawer?.querySelector<HTMLButtonElement>("button");
    closeBtn?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        setMenuOpen(false);
        return;
      }
      if (e.key === "Tab" && drawer) {
        const focusables = drawer.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [mobileOpen]);

  return (
    <header className={`sticky inset-x-0 top-0 z-50 bg-White transition-shadow duration-300 ${scrolled ? "shadow-md" : ""}`}>
      {/* Ana bar */}
      <div className="flex h-16 w-full items-center justify-between gap-2 bg-Background-light px-3 sm:h-20 sm:gap-3 sm:px-6 lg:px-10">
        <div className="flex min-w-0 items-center gap-2">
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-Primary-dark transition hover:bg-White active:scale-95 lg:hidden"
            aria-label={mobileOpen ? "Menüyü kapat" : "Menüyü aç"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-drawer"
          >
            <span aria-hidden="true" className="flex h-[22px] w-[22px] items-center justify-center">
              {mobileOpen ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-6 w-6">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </span>
          </button>
          <Link href="/" className="flex min-w-0 shrink-0 items-center" aria-label="ONOMAC ana sayfa">
            <Image
              src="/images/onomac.svg"
              alt="ONOMAC"
              width={224}
              height={36}
              priority
              className="h-8 w-auto max-w-[40vw] object-contain sm:h-9 sm:max-w-[224px]"
            />
          </Link>
        </div>

        <div className="hidden w-96 max-w-full flex-1 justify-center px-2 md:flex lg:flex-none">
          <div className="w-full max-w-96">
            <SearchBar />
          </div>
        </div>

        <div className="flex h-10 shrink-0 items-center gap-5 px-1 sm:gap-8 sm:px-4">
          <div className="group relative">
            <Link
              href="/hesabim"
              aria-label="Hesabım"
              aria-haspopup="menu"
              className="flex items-center justify-center text-Primary-dark transition hover:text-Primary"
            >
              <UserIcon />
            </Link>
            <div className="invisible absolute right-0 top-full z-50 w-48 translate-y-1 pt-2 opacity-0 transition-all group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100">
              <nav aria-label="Hesap menüsü" className="overflow-hidden rounded-2xl bg-White shadow-xl outline outline-1 outline-Background-dark">
                <Link href="/hesabim" className="block px-4 py-2.5 text-sm font-medium text-800 transition hover:bg-Background-light hover:text-Primary">Hesabım</Link>
                <Link href="/siparisler" className="block px-4 py-2.5 text-sm font-medium text-800 transition hover:bg-Background-light hover:text-Primary">Siparişlerim</Link>
                <Link href="/favoriler" className="block px-4 py-2.5 text-sm font-medium text-800 transition hover:bg-Background-light hover:text-Primary">Favorilerim{wishCount > 0 ? ` (${wishCount})` : ""}</Link>
                {isAuthenticated ? (
                  <button onClick={() => logout()} className="block w-full px-4 py-2.5 text-left text-sm font-medium text-800 transition hover:bg-Background-light hover:text-Primary">Çıkış Yap</button>
                ) : (
                  <Link href="/hesabim/giris" className="block px-4 py-2.5 text-sm font-medium text-Primary transition hover:bg-Background-light">Giriş Yap</Link>
                )}
              </nav>
            </div>
          </div>
          <Link
            href="/favoriler"
            aria-label={wishCount > 0 ? `Favorilerim, ${wishCount} ürün` : "Favorilerim"}
            className="flex items-center justify-center text-Primary-dark transition hover:text-Primary"
          >
            <HeartIcon />
          </Link>
          <button
            onClick={openCart}
            aria-label={count > 0 ? `Sepetim, ${count} ürün — görüntüle` : "Sepetim — görüntüle"}
            className="relative flex items-center justify-center text-Primary-dark transition hover:text-Primary"
          >
            <BagIcon />
            {count > 0 && (
              <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-lg bg-Tertiary px-1 text-xs font-medium leading-4 text-White">
                {count > 99 ? "99+" : count}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobil arama */}
      <div className="bg-Background-light px-3 pb-2 sm:px-4 md:hidden">
        <SearchBar />
      </div>

      {/* Nav satırı */}
      <nav aria-label="Ana menü" className="relative border-b border-100 bg-White">
        <div className="hidden items-center justify-center lg:flex" onMouseLeave={scheduleMenuClose}>
          <div onMouseEnter={openMenu}>
            <Link
              href="/urunler"
              onFocus={openMenu}
              onClick={() => setMenuOpen(false)}
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              className={`flex items-center justify-center gap-2 rounded-[70px] px-5 py-2.5 text-sm font-medium uppercase leading-5 transition ${menuOpen ? "bg-Background-light text-Primary" : "text-Primary hover:bg-Background-light"}`}
            >
              TÜM ÜRÜNLER
              <ChevronDownIcon />
            </Link>
          </div>
          {navLinks.map((l) => (
            <Link
              key={l.label}
              href={l.href}
              onMouseEnter={scheduleMenuClose}
              className="flex items-center justify-center gap-2 rounded-[70px] px-5 py-2.5 text-sm font-medium uppercase leading-5 text-Primary transition hover:bg-Background-light"
            >
              {l.label}
            </Link>
          ))}
        </div>
        {/* Mobil haplar */}
        <div className="flex gap-1.5 overflow-x-auto px-2 py-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden lg:hidden" style={{ WebkitOverflowScrolling: "touch" }}>
          <Link href="/urunler" className="flex min-h-[44px] snap-start items-center whitespace-nowrap rounded-full bg-Background-light px-3.5 py-2 text-xs font-medium text-Primary">
            TÜM ÜRÜNLER
          </Link>
          {navLinks.map((l) => (
            <Link key={l.label} href={l.href} className="flex min-h-[44px] snap-start items-center whitespace-nowrap rounded-full bg-White px-3.5 py-2 text-xs font-medium text-800 outline outline-1 outline-100">
              {l.label}
            </Link>
          ))}
        </div>

        {/* Mega menü paneli */}
        {menuOpen && (
          <div className="absolute inset-x-0 top-full z-40 hidden lg:block" onMouseEnter={openMenu} onMouseLeave={scheduleMenuClose}>
            <div className="mx-auto max-w-5xl px-6 pt-2">
              <div className="max-h-[70vh] overflow-y-auto rounded-[32px] bg-White shadow-2xl outline outline-1 outline-Background-dark">
                <MegaMenu onNavigate={() => setMenuOpen(false)} />
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Mobil çekmece */}
      {mobileOpen && (
        <div className="lg:hidden" role="dialog" aria-modal="true" aria-label="Menü">
          <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <div id="mobile-drawer" className="fixed left-0 top-0 z-50 h-full w-[86vw] max-w-[360px] overflow-y-auto bg-White shadow-2xl">
            <div className="flex h-20 items-center justify-between border-b border-100 bg-Background-light px-4">
              <Image src="/images/onomac.svg" alt="ONOMAC" width={160} height={26} className="h-7 w-auto object-contain" />
              <button onClick={() => setMobileOpen(false)} aria-label="Menüyü kapat" className="flex h-11 w-11 items-center justify-center rounded-full bg-White text-Primary-dark outline outline-1 outline-Background-dark">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="space-y-4 p-4">
              <nav className="space-y-1" aria-label="Mobil menü">
                {mobileLinks.map((l) => (
                  <Link key={l.label} href={l.href} onClick={() => setMobileOpen(false)} className="flex items-center justify-between rounded-xl border border-transparent px-3 py-3 text-sm font-medium text-Primary transition hover:border-Background-dark hover:bg-Background-light">
                    {l.label} <span className="text-400" aria-hidden="true">›</span>
                  </Link>
                ))}
                <details className="group rounded-xl outline outline-1 outline-100">
                  <summary className="flex cursor-pointer list-none items-center justify-between px-3 py-3 text-sm font-medium text-Primary">
                    Kategoriler
                    <span className="text-400 transition group-open:rotate-180" aria-hidden="true">⌄</span>
                  </summary>
                  <div className="space-y-0.5 px-2 pb-2">
                    {categoriesMeta.map((c) => (
                      <Link
                        key={c.slug}
                        href={`/kategori/${c.slug}`}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center justify-between rounded-lg bg-Background-light px-3 py-2.5 text-[13px] font-medium text-800"
                      >
                        {c.name} <span className="text-400" aria-hidden="true">›</span>
                      </Link>
                    ))}
                  </div>
                </details>
                <Link href="/favoriler" onClick={() => setMobileOpen(false)} className="flex items-center justify-between rounded-xl bg-Background-light px-3 py-3 text-sm font-medium text-Primary outline outline-1 outline-Primary/10">
                  Favoriler {wishCount > 0 && `(${wishCount})`} <span aria-hidden="true">›</span>
                </Link>
                <button onClick={() => { setMobileOpen(false); openCart(); }} className="flex w-full items-center justify-between rounded-xl bg-Primary px-3 py-3 text-sm font-medium text-White">
                  Sepet {count > 0 && `(${count})`} <span aria-hidden="true">›</span>
                </button>
              </nav>
              <div className="rounded-2xl bg-Background-light p-4 outline outline-1 outline-Background-dark">
                <p className="text-sm font-semibold text-800">Yardıma mı ihtiyacın var?</p>
                <p className="mt-1 text-xs text-500">SSS ve Nasıl Yapılır ile tüm soruların yanıtı burada.</p>
                <div className="mt-3 flex gap-2">
                  <Link href="/sikca-sorulan-sorular" onClick={() => setMobileOpen(false)} className="flex-1 rounded-full bg-White px-3 py-2 text-center text-xs font-medium text-Primary outline outline-1 outline-Background-dark">SSS</Link>
                  <Link href="/iletisim" onClick={() => setMobileOpen(false)} className="flex-1 rounded-full bg-Primary px-3 py-2 text-center text-xs font-medium text-White">İletişim</Link>
                </div>
              </div>
              <div className="border-t border-100 pt-2">
                {isAuthenticated ? (
                  <button onClick={() => { setMobileOpen(false); logout(); }} className="w-full rounded-full bg-Primary py-3 text-sm font-medium text-White">Çıkış Yap</button>
                ) : (
                  <Link href="/hesabim/giris" onClick={() => setMobileOpen(false)} className="flex w-full justify-center rounded-full bg-Primary py-3 text-sm font-medium text-White">Giriş Yap</Link>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
